// Action Creator
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Constants
import { PlanTaskCardPlaceHolders } from "@ms/planner-generic-controls/lib/local/constants/StyleConstants";
import { projectManagerAgentId } from "@ms/uno-constants/lib/local/AgentConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { TaskGroupingOption } from "@ms/uno-constants/lib/local/GroupingConstants";
import { ViewName, ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
// Controls
import { Avatar, Tooltip, useAnnounce } from "@fluentui/react-components";
import { Unknown as UnknownIcon } from "@ms/planner-generic-controls/lib/local/icons";
import { AddTaskCard } from "@ms/planner-presentational-controls/lib/local/addTaskCard/AddTaskCard";
import { AddIconPlacement } from "@ms/planner-presentational-controls/lib/local/assignmentControl/AssignmentControl";
import { TaskBoardView } from "@ms/planner-presentational-controls/lib/local/boardView/TaskBoardView";
import { UserContainer } from "@ms/planner-presentational-controls/lib/local/userContainer/UserContainer";
import { TaskBoardViewPlaceHolder } from "@ms/planner-presentational-controls/lib/local/uxPlaceholders/taskBoardView/TaskBoardViewPlaceHolder";
import { ModalType } from "@ms/uno-constants/lib/local/ModalType";
// Container Controls
import { AssignmentControlContainer } from "../../controls/assignmentControl/AssignmentControlContainer";
import { StagedAssignmentControlContainer } from "../../controls/assignmentControl/StagedAssignmentControlContainer";
// Styles
import { PlannerLogo } from "@ms/planner-generic-controls/lib/local/icons/svgs/PlannerLogo";
import { usePlanTasksBoardViewStyles } from "./PlanTasksBoardView.styles";
// Utilities
import { getTimeZone } from "@ms/uno-utilities/lib/local/CalendarUtilities";
import { parseInitialsFromName } from "@ms/uno-utilities/lib/local/StringUtilities";
import { getCopilotWorkflowStatusLabel } from "../../utilities/AgentUtilities";
import { openTaskDetailsEditor } from "../../utilities/boardUtilities";
import { OperationStatus } from "@ms/planner-models/lib/local/client/operations/Enumerations";
import { EntityCollectionPermissions } from "@ms/planner-models/lib/local/client/permissions/Enumerations";
import { TaskAccessContext } from "@ms/planner-models/lib/local/client/planner/Enumerations";
import { TaskKind } from "@ms/planner-models/lib/local/client/planner/task/ITask";
import { FormerMember } from "@ms/planner-models/lib/local/client/user/FormerUser";
import { CopilotWorkflowUnifiedState } from "@ms/uno-constants/lib/local/WorkflowConstants";
// Hooks
import { useLoggers } from "@ms/planner-presentational-controls/lib/local/utilities/hooks/useLoggers";
import { useActionCreators } from "../../hooks/useActionCreators";
import { Agent, useAgent } from "../../hooks/useAgent";
import { useConfigProvider } from "../../hooks/useConfigProvider";
import { useStores } from "../../hooks/useStores";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Strings
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
import { isGroupableCustomFieldDefinition } from "@ms/planner-models/lib/local/utilities/CustomFieldDefinitionUtilities";
import { isFlagPermissionEnabled } from "@ms/planner-models/lib/local/utilities/PermissionsUtilities";
import { renderCategoryHeaderImage } from "@ms/planner-presentational-controls/lib/local/utilities/LabelUtilities";
import { generateAssigneeViewColumns, generateBucketViewColumns, generateCategoryViewColumns, generateCustomFieldDefinitionViewColumns, generateDateViewColumns, generateGoalsViewColumnsWithOrder, generatePriorityViewColumns, generateProjectManagerWorkflowStateViewColumns, generateTaskStatusViewColumns } from "@ms/planner-presentational-controls/lib/local/utilities/taskBoard/TaskBoardColumnUtilities";
import { canAccessPlanViaContainers, getContainerMembersFromStores } from "@ms/planner-stores/lib/local/utilities/ContainerUtilities";
import { getChecklistItemPermissionsFromStores, getIsPlanCreationInProgress, getPlanPermissionsFromStores, getTaskPermissionsFromStores } from "@ms/planner-stores/lib/local/utilities/PermissionsUtilities";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { toViewIdString } from "@ms/uno-utilities/lib/local/ViewUtilities";
import React, { useRef, useState } from "react";
const COMPONENT_NAME = "PlanTasksBoardView";
/**
 * PlanTasksBoardView component - A functional component that displays plan tasks in a board view
 * This component uses Fluent v9 patterns and hooks from the container-controls package
 */ export const PlanTasksBoardView = ({ autoFocus, viewId, isSlimView })=>{
    const [appContextStore, bucketStore, driveItemStore, goalStore, permissionsStore, planStore, userStore, viewStore, taskStore, modalStore, modernGroupsStore, rosterStore, teamsChannelStore, todoStore, performanceCollectionStore, customFieldDefinitionStore] = useStores([
        Store.AppContext,
        Store.Bucket,
        Store.DriveItem,
        Store.Goal,
        Store.Permissions,
        Store.Plan,
        Store.User,
        Store.View,
        Store.Tasks,
        Store.Modal,
        Store.ModernGroups,
        Store.Roster,
        Store.TeamsChannel,
        Store.Todo,
        Store.PerformanceCollection,
        Store.CustomFieldDefinition
    ]);
    const [bucketAc, linksAc, modalAc, notificationAc, planAc, planViewAc, taskAc, userAc, viewTransformsAc] = useActionCreators([
        ActionCreator.Bucket,
        ActionCreator.Links,
        ActionCreator.Modal,
        ActionCreator.Notification,
        ActionCreator.Plan,
        ActionCreator.PlanView,
        ActionCreator.Task,
        ActionCreator.User,
        ActionCreator.ViewTransforms
    ]);
    const configProvider = useConfigProvider();
    const { engagementLogger, traceLogger } = useLoggers();
    const { announce } = useAnnounce();
    // Load agents
    const [projectManagerAgent] = useAgent([
        Agent.ProjectManager
    ]);
    const agents = [];
    if (projectManagerAgent) {
        agents.push(projectManagerAgent);
    }
    /**
     * Tracks whether the AddTaskCard is opened from the add task button
     * - true = when the "+ Add task" button is clicked to display AddTaskCard
     * - false = when AddTaskCard is closed or displayed via another way (ie. empty plan)
     */ const [isAddTaskCardOpenedFromAddTaskButton, setIsAddTaskCardOpenedFromAddTaskButton] = useState(false);
    /**
     * Indicates when a card is being dragged to a different classification (primary = Active task, secondary = completed tasks section)
     */ const isCardChangingCompletedSectionRef = useRef(false);
    /**
     * Tracks a pending agent assignment for the add task card.
     * When set, the agent will be assigned after the task is created.
     */ const [pendingAgentId, setPendingAgentId] = useState(null);
    const viewPartitionKey = viewId.partitionKey;
    const currentPlan = planStore.getPlan(viewPartitionKey).data;
    const planCreationStatus = planStore.getCreatePlanFromTemplateOperationStatus(viewPartitionKey);
    const isPlanCreationInProgress = planCreationStatus === OperationStatus.Running;
    const currentViewRenderState = performanceCollectionStore.getViewRenderState(viewId);
    if (currentPlan === null || currentViewRenderState === ViewRenderState.None || isPlanCreationInProgress) {
        return /*#__PURE__*/ React.createElement(TaskBoardViewPlaceHolder, {
            numberOfTaskCardsPerColumn: PlanTaskCardPlaceHolders,
            showAddTaskButtonShimmer: true
        });
    }
    const enableCustomFields = configProvider().flights.EnableCustomFields;
    const bucketStoreResult = bucketStore.getBucketsForPlan(viewPartitionKey);
    const isToDoPlan = currentPlan.container.containerType === ContainerType.ToDo;
    const currentTaskGrouping = viewStore.getCurrentTaskGroupingState(viewId);
    const boardViewData = taskStore.getTasksForBoardView({
        name: ViewName.PlanPage,
        partitionKey: viewPartitionKey
    }, currentTaskGrouping);
    const categories = currentPlan.planView.categories;
    const currentUserId = appContextStore.getAppContext().userId ?? "";
    const containerUtilitiesStores = {
        userStore,
        modernGroupsStore,
        rosterStore,
        teamsChannelStore,
        todoStore,
        appContextStore,
        driveItemStore,
        planStore
    };
    const permissionsUtilitiesStores = {
        ...containerUtilitiesStores,
        permissionsStore
    };
    /**
     * Create an add task card control provider with the current information
     */ const addCardControlProvider = (columnValue, sortAfter, sortBefore, valueSetter, toggleAddCardControl)=>{
        return /*#__PURE__*/ React.createElement(AddTaskCard, {
            autoFocus: isAddTaskCardOpenedFromAddTaskButton,
            createTask: (task)=>{
                // Capture and clear pending agent assignment before creating the task
                const agentIdToAssign = pendingAgentId;
                if (agentIdToAssign) {
                    setPendingAgentId(null);
                }
                planViewAc.createTask(task, columnValue, sortAfter, sortBefore, currentTaskGrouping, agentIdToAssign ?? undefined);
            },
            planId: viewPartitionKey,
            taskKind: isToDoPlan ? TaskKind.ToDo : TaskKind.Planner,
            toggleAddCard: toggleAddCardControl,
            closeOnKeyPress: false,
            buckets: bucketStoreResult.data,
            defaultDueDate: undefined,
            hideSetAssignments: currentTaskGrouping === TaskGroupingOption.Assignee,
            hideSetBucket: currentTaskGrouping === TaskGroupingOption.Bucket,
            hideSetDueDate: currentTaskGrouping === TaskGroupingOption.DueDate,
            alertUser: (titleSelector)=>{
                notificationAc.showToast({
                    title: Strings[titleSelector],
                    intent: "error",
                    isHidden: true
                });
            },
            timezone: getTimeZone(),
            renderAssignmentControlContainer: (task, userAssignments, onUserAssignmentsChanged, customStyles)=>{
                return /*#__PURE__*/ React.createElement(StagedAssignmentControlContainer, {
                    task: task,
                    userAssignments: userAssignments,
                    onUserAssignmentsChanged: onUserAssignmentsChanged,
                    showPlaceholderMessage: true,
                    strings: {
                        assignControlLauncherText: Strings.TaskAssignments_AssignControlLauncherText
                    },
                    maxDisplayablePersonas: configProvider().settings.clientBasicLimits.maxDisplayablePersonasAddTaskCard,
                    customStyles: customStyles,
                    hideSingleAssigneeDetails: false,
                    addIconPlacement: AddIconPlacement.BeforeAssignees,
                    hideFacepileBar: false,
                    agentOverride: {
                        onAssignAgent: (agentId)=>setPendingAgentId(agentId),
                        onUnassignAgent: ()=>setPendingAgentId(null),
                        assignedAgent: pendingAgentId ? agents.find((a)=>a.id === pendingAgentId) ?? null : null
                    }
                });
            }
        });
    };
    // Generate the taskboard callbacks
    const taskBoardCallbacks = {
        valueSetter: (task, columnValue, sortAfter, sortBefore, sourceColumnValue)=>{
            // Special handling for Project Manager workflow state grouping
            if (currentTaskGrouping === TaskGroupingOption.ProjectManagerWorkflowState && columnValue !== sourceColumnValue) {
                if (columnValue === CopilotWorkflowUnifiedState.InProgress) {
                    // Dragging to in progress column - assign task to agent
                    announce(Strings.CopilotStatus_WorkingOnIt, {
                        polite: false
                    });
                    taskAc.assignTaskToAgent(task.id, projectManagerAgentId);
                } else if (columnValue === CopilotWorkflowUnifiedState.Unassigned) {
                    // Dragging to Unassigned column - unassign task from agent
                    taskAc.unassignTaskFromAgent(task.id, projectManagerAgentId);
                }
            } else if (// Special handling for PMA Assignee grouping
            currentTaskGrouping === TaskGroupingOption.Assignee && columnValue === projectManagerAgentId && columnValue !== sourceColumnValue) {
                // Dragging to project manager agent column - assign task to agent
                announce(Strings.CopilotStatus_WorkingOnIt, {
                    polite: false
                });
                taskAc.assignTaskToAgent(task.id, projectManagerAgentId);
            } else {
                // Default behavior for all other groupings
                planViewAc.updateTask(task, columnValue, sortAfter, sortBefore, sourceColumnValue, currentTaskGrouping, isCardChangingCompletedSectionRef.current, ()=>{
                    // Reset the ref after the update is completed
                    isCardChangingCompletedSectionRef.current = false;
                });
            }
        },
        secondaryClassifier: (task)=>{
            return currentTaskGrouping !== TaskGroupingOption.Status ? task.isComplete() : false;
        },
        secondaryValueSetter: (task, useSecondaryValue)=>{
            if (currentTaskGrouping !== TaskGroupingOption.Status) {
                completedSectionValueSetter(task, useSecondaryValue);
            }
        },
        secondarySectionTitleProvider: (isVisible)=>{
            return Strings.TaskBoardColumn_CompletedSectionText;
        },
        onColumnDrop: (columnValue, sortAfter, sortBefore)=>{
            if (currentTaskGrouping === TaskGroupingOption.Bucket) {
                planViewAc.reorderBucket(columnValue, sortAfter, sortBefore);
            }
        },
        addCardControlProvider
    };
    /**
     * Opens the task details editor
     * @param taskId Id of task to open details for
     * @param onDismiss Callback function called when TaskDetailsEditor is closed
     * @param tab Optional tab to open in the task editor
     */ const onOpenTaskDetailsEditor = async (taskId, onDismiss, tab)=>{
        await openTaskDetailsEditor(taskId, TaskAccessContext.Plan, modalStore, modalAc, onDismiss, tab);
    };
    /**
     * Callback when a card on a board with completed section is dropped
     * @param task The task that is being dropped
     * @param [useSecondaryValue] Indicates whether the card is dropped on secondary section or not
     */ const completedSectionValueSetter = (task, useSecondaryValue)=>{
        if (useSecondaryValue !== task.isComplete()) {
            // Set state to indicate that there is a change between Active section -> Completed Section only if it is changing between sections
            isCardChangingCompletedSectionRef.current = true;
        }
    };
    /**
     * Get the header image for the uncategorized bucket. An info icon with tooltip
     */ const renderUncategorizedBucketHeaderImage = ()=>{
        if (isToDoPlan) {
            return undefined;
        }
        return /*#__PURE__*/ React.createElement(Tooltip, {
            content: Strings.Bucket_NullBucketInfoIconText,
            relationship: "label",
            positioning: "above",
            withArrow: true
        }, /*#__PURE__*/ React.createElement("div", {
            role: "img"
        }, /*#__PURE__*/ React.createElement(UnknownIcon, null)));
    };
    /** Get the user by id
     * @param userId The user id
     */ const getUser = (userId)=>{
        return userStore.getUserById(userId).data;
    };
    /**
     * Called when user avatar card is loaded
     */ const onLoadAssigneeUser = (user)=>{
        const storeImageUrl = userStore.getUserPhoto(user.id).data;
        if (!storeImageUrl || storeImageUrl === FormerMember.Photo) {
            userAc.fetchPhotosForUsers([
                user.id
            ]);
        }
    };
    /**
     * Generate the header image for an assignee column
     * @param user IUser to generate the header image for
     */ const renderAssigneeHeaderImage = (user)=>{
        const imageUrl = userStore.getUserPhoto(user.id).data;
        return /*#__PURE__*/ React.createElement(UserContainer, {
            user: user,
            onLoad: onLoadAssigneeUser,
            containerId: `userContainer-assigneeHeader-${user.id}`
        }, /*#__PURE__*/ React.createElement(Avatar, {
            color: "colorful",
            name: user.displayName,
            initials: parseInitialsFromName(user.displayName),
            image: imageUrl ? {
                src: imageUrl,
                alt: user.displayName
            } : undefined
        }));
    };
    /**
     *  Get permissions for a plan
     */ const getPlanPermissions = ()=>{
        return getPlanPermissionsFromStores(currentPlan, permissionsUtilitiesStores, traceLogger);
    };
    /**
     * Render the header image for a project manager workflow column
     * @param columnPivotValue - The copilot workflow state to render image for
     * @returns JSX element containing the header image or null
     */ const ProjectManagerHeaderImage = ({ columnPivotValue })=>{
        const styles = usePlanTasksBoardViewStyles();
        if (columnPivotValue !== CopilotWorkflowUnifiedState.InProgress) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(PlannerLogo, {
            className: styles.projectManagerLogo
        });
    };
    /**
     * Render the header image for an agent column
     * @param agent IAgent to generate the header image for
     */ const renderAgentHeaderImage = (agent)=>{
        if (agent.id === projectManagerAgentId) {
            return /*#__PURE__*/ React.createElement(ProjectManagerHeaderImage, {
                columnPivotValue: CopilotWorkflowUnifiedState.InProgress
            });
        }
        return undefined;
    };
    let columnDefinitions = [];
    let columnCreationEnabled = false;
    const bucketPermissions = permissionsStore.getBucketPermissions(currentPlan, canAccessPlanViaContainers(currentPlan, permissionsUtilitiesStores), getIsPlanCreationInProgress(currentPlan.id, permissionsUtilitiesStores));
    const buildColumns = ()=>{
        switch(currentTaskGrouping){
            case TaskGroupingOption.Bucket:
                const planPermissions = getPlanPermissions();
                const planBuckets = bucketStoreResult.data;
                const enableBucketColors = configProvider().flights.EnableBucketColors;
                columnDefinitions = generateBucketViewColumns(boardViewData, planBuckets, true, isToDoPlan ? Strings.Bucket_NullBucketLabelForTodo : Strings.Bucket_NullBucketLabel, bucketPermissions, renderUncategorizedBucketHeaderImage, (originalBucket, updatedTitle)=>{
                    const updatedBucket = originalBucket.setProperty("displayName", updatedTitle);
                    bucketAc.updateBucket(originalBucket, updatedBucket);
                }, enableBucketColors && bucketPermissions.canUpdateDisplayName ? (originalBucket, color)=>{
                    const updatedBucket = originalBucket.setBucketColor(color);
                    bucketAc.updateBucket(originalBucket, updatedBucket);
                } : undefined, enableBucketColors);
                columnCreationEnabled = isFlagPermissionEnabled(planPermissions.bucketPermissions, EntityCollectionPermissions.EnableAdd);
                break;
            case TaskGroupingOption.Status:
                columnDefinitions = generateTaskStatusViewColumns(boardViewData, true, false);
                break;
            case TaskGroupingOption.Priority:
                columnDefinitions = generatePriorityViewColumns(boardViewData, false);
                break;
            case TaskGroupingOption.Assignee:
                const storeUsers = getContainerMembersFromStores(currentPlan.container, containerUtilitiesStores, traceLogger);
                columnDefinitions = generateAssigneeViewColumns(boardViewData, storeUsers, getPlanPermissions().canUseAgents ? agents : [], false, appContextStore.getAppContext().userId ?? "", {} /** TODO: provide a map of agent IDs to their info content */ , getUser, renderAgentHeaderImage, renderAssigneeHeaderImage);
                break;
            case TaskGroupingOption.Category:
                columnDefinitions = generateCategoryViewColumns(boardViewData, viewPartitionKey, categories, true, configProvider().settings.clientBasicLimits.maxCategoryNameLength, planViewAc.updateCategoryTitle, (categoryIndex)=>renderCategoryHeaderImage(categories, categoryIndex));
                break;
            case TaskGroupingOption.DueDate:
                columnDefinitions = generateDateViewColumns(boardViewData, false, true);
                break;
            case TaskGroupingOption.FinishDate:
                break;
            case TaskGroupingOption.ProjectManagerWorkflowState:
                const canAssignToAgent = getPlanPermissions().canAssignAgents;
                columnDefinitions = generateProjectManagerWorkflowStateViewColumns(boardViewData, true, canAssignToAgent, (columnPivotValue)=>/*#__PURE__*/ React.createElement(ProjectManagerHeaderImage, {
                        columnPivotValue: columnPivotValue
                    }));
                break;
            case TaskGroupingOption.Goal:
                const planTasks = taskStore.getTasksForPlan(viewPartitionKey).data;
                const planGoalsResult = goalStore.getGoalsForPlan(currentPlan.id);
                const planGoals = planGoalsResult.data;
                const sortedGoals = [
                    ...planGoals
                ].sort((a, b)=>{
                    if (a.priority !== b.priority) {
                        return a.priority - b.priority;
                    }
                    return a.id.localeCompare(b.id);
                });
                columnDefinitions = generateGoalsViewColumnsWithOrder(planTasks, false, sortedGoals, viewPartitionKey, (planId, goalId)=>taskStore.getTasksForGoal(planId, goalId));
                break;
            case TaskGroupingOption.Plan:
            case TaskGroupingOption.None:
                traceLogger.logTrace(0x1e158143 /* tag_4fyfd */ , TraceLevel.Warning, "Invalid task grouping state for plan board view");
                break;
            default:
                if (enableCustomFields) {
                    const customFieldDefinition = customFieldDefinitionStore.getCustomFieldDefinition(currentTaskGrouping).data;
                    if (customFieldDefinition) {
                        if (isGroupableCustomFieldDefinition(customFieldDefinition.valueType)) {
                            columnDefinitions = generateCustomFieldDefinitionViewColumns(boardViewData, customFieldDefinition, false);
                        } else {
                            traceLogger.logTrace(0x1e008000 /* tag_4aiaa */ , TraceLevel.Warning, "Unsupported custom field type for task grouping: " + customFieldDefinition.valueType);
                        }
                    } else {
                        traceLogger.logTrace(0x1e006605 /* tag_4agyf */ , TraceLevel.Error, "Custom field definition not found: " + currentTaskGrouping);
                    }
                } else {
                    traceLogger.logTrace(0x1e15735c /* tag_4fxn2 */ , TraceLevel.Error, "Unreachable case hit in task board column generation: " + currentTaskGrouping);
                }
                break;
        }
    };
    buildColumns();
    /**
     * Get permissions for a task.
     * @param task Task
     * @returns Task permissions
     */ const getTaskPermissions = (task)=>{
        return getTaskPermissionsFromStores(task, planStore.getPlan(task.planId).data, permissionsUtilitiesStores, traceLogger);
    };
    const getChecklistPermissions = (task)=>{
        const plan = planStore.getPlan(task.planId).data;
        return getChecklistItemPermissionsFromStores(task, plan, permissionsUtilitiesStores, traceLogger);
    };
    /**
     * Check if copy is disabled by sensitivity label
     * @param task Task to check
     * @returns True if copy is disabled by sensitivity label
     */ const getDisableCopyTaskBySensitivity = (task)=>{
        const taskPermissions = getTaskPermissions(task);
        const planPermissions = permissionsStore.getPlanPermissionsFromUserRightsObject(task.planId);
        return taskPermissions.canCopy === false && planPermissions.canExportAllowedBySensitivity === false;
    };
    const taskBoardCardCallbacks = {
        fetchPlanData: (planId)=>{
            planAc.fetchPlan(planId);
        },
        getTaskPlan: (planId)=>{
            return planStore.getPlan(planId).data;
        },
        getTaskBucket: (bucketId)=>{
            return bucketStore.getBucket(bucketId).data;
        },
        taskCallbacks: {
            onUpdateTask: (originalTask, updatedTask)=>{
                engagementLogger.logEngagement(COMPONENT_NAME, EngagementTelemetryConstants.UpdateTask);
                taskAc.updateTask(originalTask, updatedTask);
            },
            getBuckets: (planId)=>{
                return bucketStore.getBucketsForPlan(planId).data;
            },
            openModal: modalAc.openModal,
            onCopyLinkToTask: taskAc.copyLinkToTask,
            toggleMyDayForTask: taskAc.toggleMyDayForTask,
            getUser,
            getUserPhoto: (userId)=>{
                return userStore.getUserPhoto(userId).data;
            },
            onLoadAssigneeUser,
            getCopilotWorkflowStatusLabel: (taskId)=>{
                const task = taskStore.getTask(taskId).data;
                if (!task) {
                    return null;
                }
                const canUseAgents = getPlanPermissionsFromStores(currentPlan, permissionsUtilitiesStores, traceLogger).canUseAgents;
                return getCopilotWorkflowStatusLabel(task, canUseAgents);
            },
            getGoalTitle: (goalId)=>{
                const goal = goalStore.getGoal(goalId).data;
                return goal?.displayName ?? null;
            },
            onTaskContextMenuOpen: (planId)=>{
                planAc.fetchPlanSensitivityUserRights(planId);
            },
            setLabelsCollapsedState: (isCollapsed)=>{
                viewTransformsAc.setCategoryLabelMode(TaskAccessContext.Plan, viewPartitionKey, isCollapsed);
            },
            getDocumentPreviewThumbnail: (groupId, itemUrl)=>{
                return driveItemStore.getDriveItemThumbnail(groupId, itemUrl).data;
            },
            fetchThumbnailsForTasksWithAttachments: (taskId)=>{
                taskAc.fetchTaskContentLinkThumbnails(taskId);
            },
            onOpenLink: (contentLink)=>{
                linksAc.openContentInHost(contentLink);
            },
            showInfoBanner: (notificationId)=>{
                notificationAc.showNotification({
                    notificationId,
                    messageType: "info"
                });
            },
            renderAssignmentControlContainer (task, customStyles) {
                return /*#__PURE__*/ React.createElement(AssignmentControlContainer, {
                    task: task,
                    addIconPlacement: AddIconPlacement.BeforeAssignees,
                    hideFacepileBar: false,
                    hideSingleAssigneeDetails: true,
                    strings: undefined,
                    showPlaceholderMessage: false,
                    maxDisplayablePersonas: configProvider().settings.clientBasicLimits.maxDisplayablePersonasTaskCard,
                    customStyles: customStyles
                });
            }
        }
    };
    /** Delete a task, trigger dialog for recurring tasks, giving option to delete series
     * @param taskId The id of the task to delete
     */ const deleteTask = (taskId)=>{
        const task = taskStore.getTask(taskId).data;
        if (task) {
            if (task.recurrence?.isActive()) {
                engagementLogger.logEngagement(COMPONENT_NAME, EngagementTelemetryConstants.DeleteRecurringTaskDialogOpened);
                const deleteRecurringTaskModalData = {
                    modalType: ModalType.DeleteRecurringTask,
                    taskId: taskId,
                    onDismiss: modalAc.closeModal
                };
                modalAc.openModal(deleteRecurringTaskModalData);
            } else {
                taskAc.deleteTask(task.id);
            }
        }
    };
    return /*#__PURE__*/ React.createElement(TaskBoardView, {
        autoFocus: autoFocus,
        key: toViewIdString(viewId),
        columnCreationEnabled: columnCreationEnabled,
        createColumnCallback: (displayName)=>{
            if (currentTaskGrouping === TaskGroupingOption.Bucket) {
                planViewAc.createBucket(displayName, viewPartitionKey);
            }
        },
        createColumnButtonText: Strings.TaskBoardColumn_AddNewBucket,
        createColumnPlaceholder: Strings.TaskBoardColumn_AddNewBucketPlaceHolder,
        emptyBoardTitle: Strings.TaskBoardView_EmptyBoardTitle,
        emptyBoardDescription: Strings.TaskBoardView_EmptyBoardDescription,
        columnDefinitions: columnDefinitions,
        enableColumnMove: currentTaskGrouping === TaskGroupingOption.Bucket,
        useSecondaryClassification: currentTaskGrouping !== TaskGroupingOption.Status,
        callbacks: taskBoardCallbacks,
        taskBoardCardCallbacks: taskBoardCardCallbacks,
        onOpenAddTaskCardFromAddTaskButton: ()=>{
            notificationAc.showToast({
                title: Strings.SharedComponents_AddNewTaskButtonExpanded,
                intent: "info",
                isHidden: true
            });
            setIsAddTaskCardOpenedFromAddTaskButton(true);
        },
        onCloseAddTaskCard: ()=>{
            notificationAc.showToast({
                title: Strings.SharedComponents_AddNewTaskButtonCollapsed,
                intent: "info",
                isHidden: true
            });
            setIsAddTaskCardOpenedFromAddTaskButton(false);
        },
        showPlanTitleOnCard: false,
        showCopyLinkToTask: true,
        openTaskDetails: onOpenTaskDetailsEditor,
        currentUserId: currentUserId,
        disableColumnChanges: false,
        bucketVirtualization: true,
        isSlimView: isSlimView,
        enableAutoScrollOnDragItem: true,
        enableDragToScroll: true,
        currentTaskGrouping: currentTaskGrouping,
        onDeleteTask: deleteTask,
        getTaskPermissions: getTaskPermissions,
        getChecklistPermissions: getChecklistPermissions,
        getDisableCopyTaskBySensitivity: getDisableCopyTaskBySensitivity,
        getPlanPermissions: getPlanPermissions,
        enableCopyPaste: configProvider().flights.EnableCanvasGridTaskCopyPaste
    });
};
